import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Tag} from '@/components/ui/tag'
import {HelpIcon} from '@/components/help-icon'
import {LockGlyph} from '@/components/ui/icons'
import {RegimeExpandedStamp} from '@/components/regime-expanded-stamp'
import {AuditExportLaunchButton, AuditExportStatus} from '@/components/sgsst/audit-export-status'
import {CloseControls} from '@/components/sgsst/close-controls'
import {ScoreCard} from '@/components/sgsst/score-card'
import {TerminarControls} from '@/components/sgsst/terminar-controls'
import type {SgsstAssessment, SgsstState} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {registroMintrabajoDeadline} from '@/lib/plazos'
import {formatDate, formatDateOnly} from '@/lib/risk'

export function CierreTab({
  state,
  assessment,
  businessId,
  fase2,
  editable,
  godReadOnly,
  canAttest,
  firmaBlockedReason,
  planHref,
  onFinished,
}: {
  state: SgsstState
  assessment: SgsstAssessment
  businessId: string
  fase2: boolean
  editable: boolean
  godReadOnly: boolean
  canAttest: boolean
  firmaBlockedReason: string | null
  planHref: string
  onFinished: () => void
}) {
  const {t} = useTranslation()
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <RegimeExpandedStamp
          chapter={assessment.chapter}
          prevChapter={state.periods.find((p) => p.year === assessment.period_year - 1)?.chapter}
        />
        <span className="flex items-center gap-1">
          <Tag tone="neutral" data-testid="fase-tag">
            {t(fase2 ? 'sgsst.fase.fase2' : 'sgsst.fase.fase1')}
          </Tag>
          {fase2 && (
            <HelpIcon label={t('sgsst.fase.tipLabel')} testId="fase-tip">
              {t('sgsst.fase.fase2Tip', {year: assessment.period_year})}
            </HelpIcon>
          )}
        </span>
        <span className="font-mono text-[11px] text-muted-foreground" data-testid="registro-mintrabajo-plazo">
          {t('sgsst.cycle.ministerioDeadline', {
            date: formatDateOnly(registroMintrabajoDeadline(assessment.period_year)),
          })}
        </span>
        {fase2 && (
          <Link to={planHref} className="text-sm font-medium text-primary hover:underline" data-testid="plan-link">
            {t('sgsst.planLink')}
          </Link>
        )}
        {!fase2 && editable && (
          <TerminarControls
            businessId={businessId}
            assessment={assessment}
            canAttest={canAttest}
            firmaBlockedReason={firmaBlockedReason}
            onFinished={onFinished}
          />
        )}
        {fase2 && !assessment.is_closed && !godReadOnly && (
          <CloseControls
            businessId={businessId}
            assessment={assessment}
            canAttest={canAttest}
            firmaBlockedReason={firmaBlockedReason}
          />
        )}
        <AuditExportLaunchButton businessId={businessId} year={assessment.period_year} />
        <AuditExportStatus businessId={businessId} year={assessment.period_year} />
      </div>

      {assessment.is_closed && (
        <div className="surface flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <LockGlyph />
            {t('sgsst.cycle.closedOn', {date: formatDate(assessment.closed_at!)})}
          </p>
          <Link
            to={`/businesses/${businessId}/sgsst`}
            className="text-sm font-medium text-primary hover:underline"
            data-testid="ver-ano-en-curso">
            {t('sgsst.cycle.verAnoEnCurso')}
          </Link>
        </div>
      )}

      <ScoreCard state={state} fase2={fase2} businessId={businessId} canEditWorkers={editable && !fase2} />
    </div>
  )
}
